import { state } from "./state";
import { LEVELS } from "./levels";
import { starsFor } from "./scoring";
import { $, mazeEl } from "../dom";
import { drawMaze, pathUnder } from "./grid";
import { openReveal, prepareSolves, resetReveal } from "../reveal/reveal";

/** The hero's current forced path length under the player's walls. */
export function currentLength(): number {
  const sp = pathUnder(state.level(), state.walls);
  return sp ? sp.length : state.level().base;
}

/* ---------- Level load ---------- */
export function loadLevel(i: number): void {
  state.idx = i;
  state.walls = state.saved[i].map((w) => [...w] as [number, number]);
  recompute();
  updateNav();
}

/* ---------- Recompute (render + HUD + stars + persistence) ---------- */
export function recompute(): void {
  const L = state.level();
  const sp = pathUnder(L, state.walls);
  const length = sp ? sp.length : L.base;
  const path = sp ? sp.path : [];

  drawMaze(mazeEl, L, state.walls, path, { kind: "you", interactive: true });

  // persist working walls; fold the best (longest) result into the record
  state.saved[state.idx] = state.walls.map((w) => [...w] as [number, number]);
  const rec = state.records[state.idx];
  if (length > rec.length) {
    state.records[state.idx] = { walls: state.walls.map((w) => [...w] as [number, number]), length };
  }
  const best = state.records[state.idx];

  // HUD
  $("levelVal").textContent = (state.idx + 1) + " / " + LEVELS.length;
  $("pathVal").textContent = String(length);
  $("wallsVal").textContent = state.walls.length + " / " + L.budget;
  $("levelName").innerHTML = "Labyrinth " + (state.idx + 1) + " — <b>" + L.name + "</b>";
  $("bestLine").textContent = best.length > L.base
    ? `Longest path forced so far: ${best.length} steps`
    : `Open path: ${L.base} steps — make it longer`;

  // stars rate the best (longest) result on this labyrinth
  const stars = starsFor(L, best.length);
  const starEls = $("stars").children;
  for (let i = 0; i < 3; i++) starEls[i].classList.toggle("on", i < stars);

  // message
  const msg = $("msg");
  msg.className = "msg";
  const used = state.walls.length;
  if (length >= L.opt) {
    msg.classList.add("optimal");
    msg.textContent = "✦ The longest forced path there is — the hero cannot reach the treasure one step sooner.";
  } else if (used < L.budget) {
    msg.textContent = used === 0
      ? `The hero walks ${L.base} steps. Tap a tile to raise a wall and force a longer way.`
      : `${L.budget - used} wall${L.budget - used === 1 ? "" : "s"} left — the hero is taking the cheapest way around.`;
  } else if (length >= L.twoStar) {
    msg.textContent = "A strong barrier — but a craftier placement forces an even longer path.";
  } else {
    msg.textContent = "All walls spent, yet the hero slips through quickly — the trick is which walls work together.";
  }

  if (stars === 3 && !state.completed[state.idx]) {
    state.completed[state.idx] = true;
    updateNav();
  }
  $("nextBtn").classList.toggle("glow", stars === 3);
}

/** Flash a short refusal message (budget full / would seal the treasure). */
export function flashDenied(text: string): void {
  const msg = $("msg");
  msg.className = "msg warn";
  msg.textContent = text;
}

/* ---------- Nav ---------- */
export function buildDots(): void {
  const d = $("dots");
  d.innerHTML = "";
  LEVELS.forEach((_, i) => {
    const b = document.createElement("div");
    b.className = "dot";
    b.textContent = String(i + 1);
    b.addEventListener("click", () => loadLevel(i));
    d.appendChild(b);
  });
}

export function updateNav(): void {
  const dots = $("dots").children;
  for (let i = 0; i < dots.length; i++) {
    dots[i].classList.toggle("current", i === state.idx);
    dots[i].classList.toggle("done", state.completed[i]);
    dots[i].textContent = state.completed[i] ? "✓" : String(i + 1);
  }
  ($("prevBtn") as HTMLButtonElement).disabled = state.idx === 0;
  $("nextBtn").textContent = state.idx === LEVELS.length - 1 ? "Face the optimizer ✦" : "Next ▸";
}

export function goPrev(): void {
  if (state.idx > 0) loadLevel(state.idx - 1);
}

export function goNext(): void {
  if (state.idx < LEVELS.length - 1) {
    loadLevel(state.idx + 1);
    return;
  }
  // the reveal compares each labyrinth, so every one needs to have been played
  const missing = state.records.findIndex((r) => r.length === 0);
  if (missing === -1) {
    openReveal();
  } else {
    loadLevel(missing);
    flashDenied(`Labyrinth ${missing + 1} is untouched — raise at least one wall before facing the optimizer.`);
  }
}

export function resetLevel(): void {
  state.walls = [];
  recompute();
}

/* ---------- Run lifecycle ---------- */
export function startGame(): void {
  state.phase = "play";
  loadLevel(0);
  // kick off the per-labyrinth interdiction solves (in the HiGHS Web Worker)
  // during play so the reveal is instant. Failures are swallowed here — the
  // reveal re-awaits each solve and shows a graceful message instead.
  prepareSolves().catch(() => { /* solver warm-up failed; handled at reveal time */ });
}

export function restartGame(): void {
  state.completed = LEVELS.map(() => false);
  state.saved = LEVELS.map(() => []);
  state.records = LEVELS.map(() => ({ walls: [], length: 0 }));
  $("solverOverlay").classList.add("hidden");
  $("learnOverlay").classList.add("hidden");
  resetReveal();
  loadLevel(0);
}
